import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  SeminarFilterOptionModel,
  SeminarOnlyNameModel,
} from '../../../models/seminar.model';
import { TagModel } from '../../../models/tag.model';
import { fieldRequired } from '../../../utils/form.utils';
import { getSeminarFirstDateString } from '../../../utils/seminar.utils';

export interface EditAssetDialogData {
  type: 'file' | 'link';
  currentName: string;
  currentUrl?: string;
  selectedTags: TagModel[];
  selectedSeminars: SeminarOnlyNameModel[];
  seminarOptions: SeminarFilterOptionModel[];
}

export type EditAssetDialogResult =
  | {
      type: 'file';
      name: string;
      tagIds: number[];
      seminarIds: number[];
    }
  | {
      type: 'link';
      name: string;
      url: string;
      tagIds: number[];
      seminarIds: number[];
    };

@Component({
  selector: 'app-edit-asset-dialog',
  templateUrl: './edit-asset-dialog.component.html',
  styleUrl: './edit-asset-dialog.component.scss',
  standalone: false,
})
export class EditAssetDialogComponent {
  public fieldRequired = fieldRequired;
  public type: 'file' | 'link' = 'file';
  public selectedTags: TagModel[] = [];
  public seminarOptions: SeminarFilterOptionModel[] = [];
  public getSeminarDateString = getSeminarFirstDateString;

  public form = new FormGroup({
    name: new FormControl<string>('', [
      Validators.required,
      Validators.maxLength(255),
    ]),
    url: new FormControl<string>(''),
    tagIds: new FormControl<number[]>([], Validators.required),
    seminarIds: new FormControl<number[]>([], Validators.required),
  });

  constructor(
    private dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig<EditAssetDialogData>
  ) {
    this.type = this.config.data?.type ?? 'file';
    this.selectedTags = this.config.data?.selectedTags ?? [];
    this.seminarOptions = this.config.data?.seminarOptions ?? [];

    this.configureTypeValidators();

    this.form.patchValue({
      name: this.config.data?.currentName ?? '',
      url: this.config.data?.currentUrl ?? '',
      tagIds: this.selectedTags?.map(tag => tag.id),
      seminarIds:
        this.config.data?.selectedSeminars?.map(seminar => seminar.id) ?? [],
    });
  }

  private configureTypeValidators(): void {
    if (this.type === 'link') {
      this.form
        .get('url')
        ?.setValidators([
          Validators.pattern('https?://.+'),
          Validators.maxLength(2000),
        ]);
    }
  }

  public selectedTagsChange(tags: TagModel[]): void {
    this.selectedTags = tags;
    this.form.patchValue({ tagIds: tags.map(tag => tag.id) });
    this.form.get('tagIds')?.updateValueAndValidity();
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const result: EditAssetDialogResult =
      this.type === 'file'
        ? {
            type: this.type,
            name: (this.form.value.name ?? '').trim(),
            tagIds: this.form.value.tagIds ?? [],
            seminarIds: this.form.value.seminarIds ?? [],
          }
        : {
            type: this.type,
            name: (this.form.value.name ?? '').trim(),
            url: (this.form.value.url ?? '').trim(),
            tagIds: this.form.value.tagIds ?? [],
            seminarIds: this.form.value.seminarIds ?? [],
          };
    this.dialogRef.close(result);
  }
}
